import React, { useCallback, useContext, useMemo } from 'react';
import ComparisonSelector from './ComparisonSelector';
import ComparisonContext from '../../../contexts/ComparisonContext';
import AnalyticsContext from '../../../contexts/AnalyticsContext';
import ReportContext from '../../../contexts/ReportContext';
import ReportGadgetsContext from '../../../contexts/ReportGadgetsContext';
import isSingleMetricDateMatrix from '../../../types/visTypeCheckers/isSingleMetricDateMatrix';
import CardContext from '../../../contexts/CardContext';
import PopupContext from '../../../contexts/PopupContext';
import isDefined from '../../../isDefined';
import cardTypeCheckers from '../../../types/cardTypeCheckers';
import isPaceMatrix from '../../../types/visTypeCheckers/isPaceMatrix';
import { V3SelectedAppContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';

const useHasCompareToVis = ({
  isPaceForm,
  isSingleMetricDateMatrixForm,
}: {
  isPaceForm?: boolean;
  isSingleMetricDateMatrixForm?: boolean;
}): {
  hasPace: boolean;
  hasSingleMetricDateMatrix: boolean;
} => {
  const { report } = useContext(ReportContext);
  const { chartDefinition } = useContext(CardContext);
  const { allGadgets } = useContext(ReportGadgetsContext);
  const canvasCharts = useMemo((): VisualisationDefinition[] => {
    if (!report) {
      return [];
    }

    return report.canvas.cards
      .filter(cardTypeCheckers.isReportGadget)
      .map((c) => {
        const gadget = allGadgets.find(
          (g) => g.id === c.content.reportGadgetId,
        );
        return gadget ? gadget.chartDef : undefined;
      })
      .filter(isDefined);
  }, [allGadgets, report]);

  const hasPace = useMemo(() => {
    if (isPaceForm) {
      return true;
    }

    if (chartDefinition) {
      return isPaceMatrix(chartDefinition);
    }

    return canvasCharts.some((c) => isPaceMatrix(c));
  }, [isPaceForm, canvasCharts, chartDefinition]);

  const hasSingleMetricDateMatrix = useMemo(() => {
    if (isSingleMetricDateMatrixForm) {
      return true;
    }

    if (chartDefinition) {
      return isSingleMetricDateMatrix(chartDefinition);
    }

    return canvasCharts.some((c) => isSingleMetricDateMatrix(c));
  }, [isSingleMetricDateMatrixForm, canvasCharts, chartDefinition]);

  return {
    hasPace,
    hasSingleMetricDateMatrix,
  };
};

const ComparisonSelectorContainer = ({
  isReport,
  isOnlyPreviousAllowed,
  isPaceForm,
  isSingleMetricDateMatrixForm,
}: {
  isReport?: boolean;
  isOnlyPreviousAllowed?: boolean;
  isPaceForm?: boolean;
  isSingleMetricDateMatrixForm?: boolean;
}) => {
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const isBonusApp =
    !!selectedProductApp && appTypeCheckers.isBonusApp(selectedProductApp);
  const { isOpen: isPopupReportOpen } = useContext(PopupContext);
  const { hasPace, hasSingleMetricDateMatrix } = useHasCompareToVis({
    isPaceForm,
    isSingleMetricDateMatrixForm,
  });
  const { currentComparison, setCurrentComparison } =
    useContext(ComparisonContext);
  const { trackEvent } = useContext(AnalyticsContext);

  const track = useCallback(() => {
    if (isReport && !isPopupReportOpen) {
      trackEvent('Report - Comparison Changed');
    }
  }, [isPopupReportOpen, isReport, trackEvent]);

  if (!setCurrentComparison) {
    throw new Error('setCurrentComparison not available in context');
  }

  return (
    <ComparisonSelector
      currentComparison={currentComparison}
      setCurrentComparison={setCurrentComparison}
      track={track}
      hasPace={hasPace}
      hasSingleMetricDateMatrix={hasSingleMetricDateMatrix}
      isOnlyPreviousAllowed={!!isOnlyPreviousAllowed || isBonusApp}
      isPaceForm={isPaceForm}
      isSingleMetricDateMatrixForm={isSingleMetricDateMatrixForm}
    />
  );
};

export default ComparisonSelectorContainer;
